import * as React from "react";
import { useParams } from "react-router";

import { Container, Grid, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import TranslateIcon from "@mui/icons-material/Translate";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import PaperFragment from "../../../helper-components/misc/PaperFragment.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";

import { isEmpty } from "../../../helper-functions/shared/miscUtils.js";
import { queryTextTranslation } from "../../../helper-functions/api/translationQueries.js";

import { getLanguage } from "../../../config/language/language.js";

/**
 * A page detailing a translated text.
 *
 * Includes:
 * - formatted transcript text translation
 */
export default function TextTranslationView(props) {
    const { id } = useParams(); // Get the URL parameters from react-router.

    const [textTranslation, setTextTranslation] = React.useState({});
    const [textTranslationQuery, setTextTranslationQuery] = React.useState(false);
    const [originalTextQuery, setOriginalTextQuery] = React.useState(false);
    const [showOriginal, setShowOriginal] = React.useState(false);

    const textTranslations = props?.backend?.textTranslations?.data;

    const hasOriginal = !isEmpty(textTranslation?.original_text);
    const displayedText = showOriginal && hasOriginal ? textTranslation.original_text : textTranslation?.text || "";
    const isReady = !isEmpty(textTranslation) && !isEmpty(displayedText);
    const resource = textTranslation?.resource;

    // Set the text translation, and potentially query it if it's not available.
    React.useEffect(() => {
        const possibleTextTranslation = textTranslations.find((t) => t.id === parseInt(id));

        if (!isEmpty(possibleTextTranslation)) {
            setTextTranslationQuery(true);
            setTextTranslation(possibleTextTranslation);

            if (possibleTextTranslation.original_text === undefined && !originalTextQuery) {
                setOriginalTextQuery(true);
                queryTextTranslation(props, { id });
            }
        }

        if (isEmpty(possibleTextTranslation) && !textTranslationQuery) {
            setTextTranslationQuery(true);
            queryTextTranslation(props, { id });
        }
    }, [textTranslations, textTranslation]);

    const handleCopyToClipboard = () => {
        navigator.clipboard
            .writeText(resource?.name + "\n\n" + displayedText)
            .then(() => {
                props.showSnackbar({ message: "Text copied to clipboard!", color: "info" });
            })
            .catch((error) => {
                console.error("Failed to copy: ", error);
                props.showSnackbar({ message: "Failed to copy text", color: "error" });
            });
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <CustomForm
                        title={getLanguage().TEXT_TRANSLATION_VIEW_TITLE}
                        subtitle={getLanguage().TEXT_TRANSLATION_VIEW_SUBTITLE}
                        icon={<TranslateIcon />}
                        form={
                            <Stack direction="row" mt={2} spacing={1} flexWrap="wrap" useFlexGap>
                                <CustomButton
                                    {...props}
                                    title="Open URL"
                                    size="small"
                                    onClick={() => window.open(resource?.url, "_blank")}
                                    disabled={!resource?.url}
                                    variant="outlined"
                                />
                                <CustomButton {...props} title="Copy" size="small" onClick={handleCopyToClipboard} disabled={!isReady} variant="outlined" />
                                {hasOriginal && (
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={showOriginal ? "original" : "translated"}
                                        onChange={(event, value) => {
                                            if (!value) return;
                                            setShowOriginal(value === "original");
                                        }}>
                                        <ToggleButton value="translated">Translated</ToggleButton>
                                        <ToggleButton value="original">Original</ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            </Stack>
                        }
                    />
                </Grid>

                {isReady && (
                    <Grid item xs={12}>
                        <PaperText bold center value={resource?.name || "Title: N/A"} />
                        {textTranslation?.meta_data?.instructions && (
                            <PaperText fontStyle="italic" center value={`Additional instructions: ${textTranslation?.meta_data?.instructions}`} />
                        )}
                    </Grid>
                )}

                {textTranslationQuery && !isReady && (
                    <Grid item xs={12}>
                        <PaperText value={getLanguage().NO_TEXT_TRANSLATION_FOUND} />
                    </Grid>
                )}

                {isReady && (
                    <Grid item xs={12}>
                        <PaperFragment p={1.5} value={<div style={{ whiteSpace: "pre-wrap" }}>{displayedText || "N/A"}</div>} />
                    </Grid>
                )}

                <NavigationFooter
                    rows={2}
                    options={[
                        {
                            path: `/translations/text`,
                            title: "Back to text translations",
                            variant: "outlined",
                            icon: <TranslateIcon />
                        },
                        {
                            path: `/translations/audio`,
                            title: "Audio translations",
                            variant: "outlined",
                            icon: <AudiotrackIcon />
                        },
                        {
                            path: `/database/resources`,
                            title: "Resources",
                            variant: "outlined",
                            icon: <DescriptionIcon />
                        }
                    ]}
                />
            </Grid>
        </Container>
    );
}
